import { HubjectPricingPlan } from '../models/hubject-pricing-plan.model';
import { HubjectProvider } from '../models/hubject-provider';
import { Globals } from '../shared/globals';
import { Injectable } from '@angular/core';
import { JsonSerializer } from 'typescript-json-serializer';
import { Api } from '../shared/api';
import { HubjectPricingProduct } from '../models/hubject-pricing-product.model';

/**
 * Service responsible for Hubject related actions.
 *
 * @export
 * @class HubjectService
 */
@Injectable()
export class HubjectService {
  private readonly jsonSerializer = new JsonSerializer();
  private readonly pricingProductsUrl: string;
  private readonly pricingUrl: string;
  private readonly evseDataUrl: string;
  private readonly evseStatusUrl: string;
  private readonly cdrsUrl: string;
  private readonly providersUrl: string;

  constructor(private globals: Globals, private api: Api) {
    this.pricingProductsUrl = this.globals.adminUrl('hubject/pricing/products');
    this.pricingUrl = this.globals.adminUrl('hubject/pricing');
    this.evseDataUrl = this.globals.adminUrl('hubject/data');
    this.evseStatusUrl = this.globals.adminUrl('hubject/status');
    this.cdrsUrl = this.globals.adminUrl('hubject/cdrs');
    this.providersUrl = this.globals.adminUrl('hubject/providers');
  }

  public async getPricingProducts(): Promise<Array<HubjectPricingProduct>> {
    const pricingProducts: string = await this.api.httpGet(
      this.pricingProductsUrl
    );
    return this.jsonSerializer.deserializeObjectArray(
      pricingProducts,
      HubjectPricingProduct
    );
  }

  public async pushPricingProducts(): Promise<void> {
    return await this.api.httpPost(this.pricingProductsUrl, {});
  }

  public async pushPricing(): Promise<void> {
    return await this.api.httpPost(this.pricingUrl, {});
  }

  public async pushEvseData(): Promise<void> {
    return await this.api.httpPost(this.evseDataUrl, {});
  }

  public async pushEvseStatus(): Promise<void> {
    return await this.api.httpPost(this.evseStatusUrl, {});
  }

  public async sendCdr(
    sessionId: string,
    hubjectUserId: number,
    date: Date
  ): Promise<void> {
    return await this.api.httpPut(`${this.cdrsUrl}/${sessionId}`, {
      HubjectUserId: hubjectUserId,
      Date: date.toISOString(),
    });
  }

  public getPricingPlans(): HubjectPricingPlan[] {
    return [
      new HubjectPricingPlan({
        id: 'LowCHF',
        priceDescription: '0.25 / kWh',
        currency: 'CHF',
      }),
      new HubjectPricingPlan({
        id: 'MediumCHF',
        priceDescription: '0.50 / kWh',
        currency: 'CHF',
      }),
      new HubjectPricingPlan({
        id: 'HighCHF',
        priceDescription: '0.70 / kWh',
        currency: 'CHF',
      }),
      new HubjectPricingPlan({
        id: 'ExtraCHF',
        priceDescription: '0.95 / kWh',
        currency: 'CHF',
      }),
      new HubjectPricingPlan({
        id: 'LowEUR',
        priceDescription: '0.25 / kWh',
        currency: 'EUR',
      }),
      new HubjectPricingPlan({
        id: 'MediumEUR',
        priceDescription: '0.50 / kWh',
        currency: 'EUR',
      }),
      new HubjectPricingPlan({
        id: 'HighEUR',
        priceDescription: '0.70 / kWh',
        currency: 'EUR',
      }),
      new HubjectPricingPlan({
        id: 'ExtraEUR',
        priceDescription: '0.95 / kWh',
        currency: 'EUR',
      }),
    ];
  }

  public hubjectPlanDescription(plan: HubjectPricingPlan): string {
    let description = plan.id;
    if (plan.priceDescription) {
      description += ` (${plan.priceDescription})`;
    }

    return description;
  }

  public async getHubjectProviders(): Promise<HubjectProvider[]> {
    const providers: string = await this.api.httpGet(this.providersUrl);

    return this.jsonSerializer.deserializeObjectArray(
      providers,
      HubjectProvider
    );
  }
}
